import { GOOGLE_API_KEY, GOOGLE_APP_ID } from "./google-config";
import {
  GAPI_SCRIPT_SRC,
  type GoogleSdk,
  gapiSdk,
  googleSdk,
  loadGoogleScript,
} from "./load-google-script";

export type GooglePickerDoc = {
  id: string;
  name?: string;
  mimeType?: string;
  url?: string;
  sizeBytes?: number | string;
};

type PickerCallbackData = { action: string; docs?: GooglePickerDoc[] };

const PICKER_ZFIX_ID = "google-picker-zfix";
const DARK_BACKDROP_CLASS = "google-picker-dark-bg";
const PICKER_LOAD_TIMEOUT_MS = 15000;

const PICKER_SCRIM_ID = "google-picker-scrim";
const PICKER_SCRIM_COLOR = "rgba(0, 0, 0, 0.56)";

const ensurePickerOnTop = () => {
  const existing = document.getElementById(PICKER_ZFIX_ID);
  const style = existing ?? document.createElement("style");
  style.id = PICKER_ZFIX_ID;
  style.textContent = `
    .picker-dialog-bg {
      z-index: 2147483000 !important;
      position: fixed !important;
      top: 0 !important; left: 0 !important;
      width: 100vw !important; height: 100vh !important;
      pointer-events: auto !important;
    }
    .${DARK_BACKDROP_CLASS} .picker-dialog-bg {
      opacity: 0 !important;
    }
    .picker-dialog {
      z-index: 2147483001 !important;
      position: fixed !important;
      top: 50% !important; left: 50% !important;
      transform: translate(-50%, -50%) !important;
      margin: 0 !important;
      max-width: 92vw !important; max-height: 90vh !important;
      pointer-events: auto !important;
    }
  `;
  if (!existing) document.head.appendChild(style);
};

const showPickerScrim = () => {
  const scrim = document.createElement("div");
  scrim.id = PICKER_SCRIM_ID;
  scrim.setAttribute("aria-hidden", "true");
  Object.assign(scrim.style, {
    position: "fixed",
    inset: "0",
    zIndex: "2147482999",
    background: PICKER_SCRIM_COLOR,
  });
  document.body.appendChild(scrim);
  return () => scrim.remove();
};

// Freeze the page behind the Picker: block wheel/touch AND undo the
// focus-jump the Picker iframe triggers.
const lockBackgroundScroll = () => {
  const prevOverflow = document.body.style.overflow;
  document.body.style.overflow = "hidden";
  const opts = { passive: false, capture: true } as AddEventListenerOptions;
  const prevent = (e: Event) => e.preventDefault();
  window.addEventListener("wheel", prevent, opts);
  window.addEventListener("touchmove", prevent, opts);

  const scroller =
    (document.scrollingElement as Element) || document.documentElement;
  const snap = new Map<Element, [number, number]>();
  document.querySelectorAll<HTMLElement>("*").forEach((el) => {
    if (el.scrollTop || el.scrollLeft)
      snap.set(el, [el.scrollTop, el.scrollLeft]);
  });
  snap.set(scroller, [scroller.scrollTop, scroller.scrollLeft]);
  const onScroll = (e: Event) => {
    const el = (
      e.target === document || e.target === window ? scroller : e.target
    ) as HTMLElement | null;
    if (!el || el.closest?.(".picker-dialog")) return;
    const p = snap.get(el);
    el.scrollTop = p ? p[0] : 0;
    el.scrollLeft = p ? p[1] : 0;
  };
  document.addEventListener("scroll", onScroll, true);

  return () => {
    document.body.style.overflow = prevOverflow;
    window.removeEventListener("wheel", prevent, opts);
    window.removeEventListener("touchmove", prevent, opts);
    document.removeEventListener("scroll", onScroll, true);
  };
};

export const openGooglePicker = ({
  token,
  buildView,
  supportDrives = false,
  darkBackdrop = false,
}: {
  token: string;
  // several views render as tabs, in order
  buildView: (picker: GoogleSdk) => unknown | unknown[];
  supportDrives?: boolean;
  darkBackdrop?: boolean;
}) =>
  new Promise<GooglePickerDoc | null>((resolve, reject) => {
    if (!GOOGLE_API_KEY) {
      reject(
        new Error("Google is not configured (VITE_GOOGLE_API_KEY missing)"),
      );
      return;
    }
    let unlockScroll = () => {};
    let settled = false;
    const settleResolve = (value: GooglePickerDoc | null) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    const settleReject = (err: unknown) => {
      if (settled) return;
      settled = true;
      unlockScroll();
      reject(err);
    };

    loadGoogleScript(GAPI_SCRIPT_SRC, () => !!gapiSdk())
      .then(() => {
        gapiSdk().load("picker", {
          callback: () => {
            if (settled) return;
            try {
              ensurePickerOnTop();
              const unlock = lockBackgroundScroll();
              const root = document.documentElement;
              let hideScrim = () => {};
              if (darkBackdrop) {
                root.classList.add(DARK_BACKDROP_CLASS);
                hideScrim = showPickerScrim();
              }
              unlockScroll = () => {
                unlock();
                root.classList.remove(DARK_BACKDROP_CLASS);
                hideScrim();
              };
              const picker = googleSdk().picker;
              const builder = new picker.PickerBuilder()
                .setAppId(GOOGLE_APP_ID)
                .setOAuthToken(token)
                .setDeveloperKey(GOOGLE_API_KEY);
              const supportDrivesFeature = picker.Feature?.SUPPORT_DRIVES;
              if (supportDrives && supportDrivesFeature)
                builder.enableFeature(supportDrivesFeature);
              for (const view of ([] as unknown[]).concat(buildView(picker)))
                builder.addView(view);
              builder
                .setCallback((data: PickerCallbackData) => {
                  if (data.action === picker.Action.PICKED) {
                    unlockScroll();
                    settleResolve(data.docs?.[0] ?? null);
                  } else if (data.action === picker.Action.CANCEL) {
                    unlockScroll();
                    settleResolve(null);
                  }
                })
                .build()
                .setVisible(true);
            } catch (err) {
              settleReject(err);
            }
          },
          onerror: (err: unknown) =>
            settleReject(
              err instanceof Error
                ? err
                : new Error("Failed to load the Google Picker"),
            ),
          timeout: PICKER_LOAD_TIMEOUT_MS,
          ontimeout: () =>
            settleReject(new Error("Timed out loading the Google Picker")),
        });
      })
      .catch((err) => settleReject(err));
  });
